import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import ConciergeCard from "../components/ConciergeCard";
import LiveAvailabilityTicker from "../components/LiveAvailabilityTicker";
import DictateMicButton from "../components/DictateMicButton";
import { FALLBACK_CONCIERGES, mergeWithFallback } from "../lib/fallbacks";
import { Sparkles, ArrowRight, Shield, MapPin, Star, MessageCircle } from "lucide-react";

const HEROES = [
  "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=1400",
  "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1400",
];

const AI_EXAMPLES = [
  "Plan my anniversary weekend.",
  "Coordinate my family vacation.",
  "Find my perfect night out.",
  "Plan my proposal.",
];

export default function Landing(){
  const { t } = useTranslation();
  const [concierges, setConcierges] = useState(FALLBACK_CONCIERGES);
  const [prompt, setPrompt] = useState("");
  const nav = useNavigate();
  useEffect(()=>{
    api.post("/seed").catch(()=>{});
    // Featured section is now PROS (the trusted experts), not experience listings.
    // We pull real elite/verified providers first, fall back to the static
    // Marcus/Sophia/David trio so the section is never empty.
    api.get("/providers?verified=true")
      .then(r => {
        const real = (Array.isArray(r.data) ? r.data : []).map(p => ({
          ...p,
          title: p.title || (
            (p.categories || []).includes("entertainment") ? "VIP & Entertainment Pro"
              : (p.categories || []).includes("travel") ? "Travel & Lifestyle Pro"
              : "Experience Pro"
          ),
          starting_at: p.price_floor || 49,
          moments_created: p.moments_created || Math.max(85, Math.round((p.earnings || 0) / 60)),
          specializes: p.specializes || p.categories || ["Birthdays", "VIP", "Corporate Events", "Celebrations"],
          photo: p.photo || p.profile_photo,
        }));
        // Surface Elite + TREL+ first so the homepage leads with the best
        const sorted = [...real].sort((a, b) => {
          const eA = a.elite_badge_active ? 2 : (a.trel_plus_active ? 1 : 0);
          const eB = b.elite_badge_active ? 2 : (b.trel_plus_active ? 1 : 0);
          return eB - eA;
        });
        // Dedupe by name+city — older seed runs left duplicate provider rows in DB
        const seen = new Set();
        const unique = sorted.filter(p => {
          const k = `${p.name}|${p.city}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        setConcierges(mergeWithFallback(unique, FALLBACK_CONCIERGES, 4));
      })
      .catch(() => setConcierges(FALLBACK_CONCIERGES));
  },[]);
  const launchAI = () => { nav(`/ai?q=${encodeURIComponent(prompt)}`); };

  return (
    <div className="bg-[#FFF8EE]">
      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-6 pt-10 pb-10 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-3 mb-6 flex-wrap">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#064E3B]">
              <span className="w-8 h-px bg-[#064E3B]"/> {t("landing.kicker")}
            </div>
            <LiveAvailabilityTicker/>
          </div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] text-[#071A2D]">
            {t("landing.headline_a")}<br/>
            <span className="italic text-[#064E3B]">{t("landing.headline_b")}</span>
          </h1>
          <p
            className="mt-7 text-sm md:text-[15px] leading-[1.5] md:leading-[1.55] tracking-[0.005em] font-normal text-[#071A2D]/80 max-w-md md:max-w-2xl hero-supporting-tagline"
            data-testid="hero-supporting-tagline"
          >
            {t("landing.subhead")}
          </p>

          <div className="mt-7 glass-ai rounded-3xl p-6 relative">
            {/* Mobile-only floating coral Mic FAB — sits above the top-right
                corner of the AI input card so a first-time mobile visitor
                can dictate their vision in a single thumb-tap.
                Hidden on desktop (≥1024px) where the keyboard is faster. */}
            <div
              className="lg:hidden absolute -top-7 right-5 z-10"
              data-testid="hero-dictate-wrapper"
            >
              <DictateMicButton
                testid="hero-dictate-mic"
                onTranscript={(text) => setPrompt(text)}
              />
            </div>
            <div className="flex items-center gap-2 text-[#00AEEF] text-xs uppercase tracking-widest mb-3">
              <Sparkles size={14}/> What do you need handled?
            </div>
            <textarea data-testid="hero-ai-input" rows={3} placeholder={t("landing.ai_placeholder")}
              className="w-full bg-transparent text-[#071A2D] placeholder-[#071A2D]/40 resize-none outline-none text-lg"
              value={prompt} onChange={e=>setPrompt(e.target.value)}/>
            <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
              <div className="flex gap-2 flex-wrap text-xs">
                {AI_EXAMPLES.slice(0, 4).map((ex) => (
                  <button
                    key={ex}
                    onClick={() => setPrompt(ex)}
                    data-testid={`hero-ai-example-${ex.slice(0, 12).replace(/[^a-z]/gi, '').toLowerCase()}`}
                    className="px-3 py-1 rounded-full bg-[#FFF8EE] border border-[#071A2D]/10 hover:border-[#064E3B] text-[#071A2D]/75"
                  >
                    {ex}
                  </button>
                ))}
              </div>
              <button onClick={launchAI} data-testid="hero-ai-submit" className="btn-coral flex items-center gap-2 whitespace-nowrap">
                {t("landing.design_btn")} <ArrowRight size={16}/>
              </button>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-6 text-sm text-[#064E3B]/70">
            <span title={t("landing.trust_verified_tooltip")} aria-label={t("landing.trust_verified_tooltip")}
              data-testid="trust-verified" className="flex items-center gap-2 cursor-help">
              <Shield size={14}/> {t("landing.trust_verified")}
            </span>
            <span className="flex items-center gap-2"><Star size={14}/> {t("landing.trust_rating")}</span>
            <span className="flex items-center gap-2"><MapPin size={14}/> {t("landing.trust_cities")}</span>
          </div>
        </div>

        {/* Hero imagery + JOIN CTAs directly underneath (right column) */}
        <div className="lg:col-span-5 fade-up-delay-2">
          {/* Image collage — taller (h-[460px]) with extra breathing room.
              Images remain the visual anchor; the CTAs below sit clearly
              underneath as the secondary action. */}
          <div className="relative h-[460px] hidden lg:block" data-testid="hero-image-collage">
            <img src={HEROES[0]} alt="" className="absolute top-0 right-0 w-[78%] h-60 object-cover rounded-3xl shadow-luxury-lg"/>
            <img src={HEROES[1]} alt="" className="absolute bottom-0 left-0 w-[78%] h-60 object-cover rounded-3xl shadow-luxury-lg"/>
            <div className="absolute bottom-6 right-0 glass-ai rounded-2xl p-3.5 w-52">
              <div className="text-[10px] uppercase tracking-widest text-[#00AEEF]">{t("landing.now_designing_kicker")}</div>
              <div className="font-medium text-sm mt-0.5">{t("landing.now_designing_title")}</div>
              <div className="text-xs text-[#064E3B]/70 mt-0.5">{t("landing.now_designing_sub")}</div>
            </div>
          </div>

          {/* Secondary luxury CTA tiles — sit lower under the hero image so the
              imagery stays the focal point. Smaller, equal-size, aligned. */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="marketplace-cta-section">
            <Link
              to="/become-provider"
              data-testid="card-become-provider"
              className="group rounded-2xl p-5 bg-[#064E3B] text-[#FFF8EE] shadow-luxury hover:-translate-y-0.5 hover:shadow-luxury-lg transition-all flex flex-col"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">{t("landing.earn")}</div>
              <h3 className="font-serif text-xl mt-2 leading-snug text-[#FFF8EE]">{t("landing.become_provider_title")}</h3>
              <p className="mt-2 text-[#FFF8EE]/80 text-xs leading-relaxed line-clamp-3">{t("landing.become_provider_body")}</p>
              <span
                data-testid="cta-become-provider"
                className="inline-flex items-center gap-1 mt-auto pt-3 text-xs font-semibold text-[#C9A45C] group-hover:translate-x-0.5 transition-transform"
              >
                {t("landing.apply_now")} →
              </span>
            </Link>
            <Link
              to="/become-host"
              data-testid="card-become-host"
              className="group rounded-2xl p-5 bg-[#FFF8EE] border border-[#071A2D]/12 shadow-luxury hover:-translate-y-0.5 hover:shadow-luxury-lg transition-all flex flex-col"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">{t("landing.property")}</div>
              <h3 className="font-serif text-xl mt-2 leading-snug text-[#071A2D]">{t("landing.host_title_short")}</h3>
              <p className="mt-2 text-[#071A2D]/70 text-xs leading-relaxed line-clamp-3">{t("landing.host_body_short")}</p>
              <span
                data-testid="cta-become-host"
                className="inline-flex items-center gap-1 mt-auto pt-3 text-xs font-semibold text-[#064E3B] group-hover:translate-x-0.5 transition-transform"
              >
                {t("landing.list_property")} →
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* HOW TREL WORKS — three-step luxury simplification.
            Single screen-glance promise: share → match → enjoy.
            Sits between the hero and the Concierge grid so first-time
            visitors understand TREL within 5 seconds. */}
      <section className="max-w-6xl mx-auto px-6 pt-6 pb-20" data-testid="how-it-works-simple">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.22em] text-[#064E3B] font-semibold">{t("landing.how_kicker")}</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#071A2D] leading-[1.05]">
            {t("landing.how_title_a")} <span className="italic text-[#064E3B]">{t("landing.how_title_b")}</span>
          </h2>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-7">
          {[
            { n: "01", icon: Sparkles,      key: "share",     accent: "#00AEEF" },
            { n: "02", icon: MessageCircle, key: "match",     accent: "#064E3B" },
            { n: "03", icon: Star,          key: "enjoy",     accent: "#C9A45C" },
          ].map((s) => (
            <div
              key={s.key}
              data-testid={`how-step-${s.key}`}
              className="card-luxury rounded-3xl p-8 md:p-10 flex flex-col items-start text-left hover:-translate-y-0.5 transition-transform duration-300"
            >
              <div
                className="w-12 h-12 rounded-full inline-flex items-center justify-center shrink-0"
                style={{ background: `${s.accent}1A`, color: s.accent }}
              >
                <s.icon size={22}/>
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B]/70 font-semibold mt-6">
                Step {s.n}
              </div>
              <h3 className="font-serif text-2xl md:text-3xl mt-2 text-[#071A2D] leading-tight">
                {t(`landing.how_${s.key}_title`)}
              </h3>
              <p className="mt-3 text-[#071A2D]/70 text-[15px] leading-relaxed">
                {t(`landing.how_${s.key}_body`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* MEET YOUR EXPERIENCE EXPERTS — concierge profile cards, surfaced right after hero */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#064E3B]">{t("landing.featured_kicker")}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-2">{t("landing.featured_title")}</h2>
            <p className="mt-2 text-[#071A2D]/65 max-w-xl">{t("landing.featured_sub")}</p>
          </div>
          <Link to="/explore" className="text-sm underline whitespace-nowrap">{t("landing.explore_all")}</Link>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {concierges.slice(0, 4).map(c => <ConciergeCard key={c.user_id} c={c}/>)}
        </div>
      </section>

      {/* WHAT'S YOUR MOMENT bento + CORPORATE block intentionally removed
          (Phase 43-C). TREL is positioned as "Life's Details, Handled." —
          customers should not browse a menu of category tiles, and the
          dark corporate panel split focus away from the single hero
          promise. The homepage now flows Hero → How → Featured Pros →
          Footer for a clean Apple+Airbnb feel. */}

      {/* Footer — nav links only; join cards live at the top under the hero image */}
      <footer className="border-t border-[#071A2D]/10 mt-2 py-10 text-center text-sm text-[#071A2D]/60">
        <div className="flex flex-wrap justify-center gap-6 mb-3">
          <Link to="/how" className="hover:text-[#064E3B]">{t("nav.how")}</Link>
          <Link to="/about" data-testid="footer-about" className="hover:text-[#064E3B]">{t("nav.about")}</Link>
          <Link to="/become-provider" className="hover:text-[#064E3B]">{t("nav.become_provider")}</Link>
          <Link to="/become-host" className="hover:text-[#064E3B]">{t("nav.become_host")}</Link>
        </div>
        © {new Date().getFullYear()} TREL — Travel. Reservations. Experiences. Lifestyle.
      </footer>
    </div>
  );
}
